import { Controller } from "@hotwired/stimulus"

// Combobox-style search: loads up to 5 customers from JSON `search` endpoint.
export default class extends Controller {
  static targets = ["hiddenInput", "queryInput", "results"]
  static values = {
    url: String,
    debounce: { type: Number, default: 300 },
  }

  connect() {
    this.timeout = null
    this.abortController = null
    this.highlightIndex = -1
    this.selectedLabel = this.queryInputTarget.value
    this._boundDocClick = this.#closeIfOutside.bind(this)
    document.addEventListener("click", this._boundDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this._boundDocClick)
    clearTimeout(this.timeout)
    this.#abortFetch()
  }

  open() {
    if (this.queryInputTarget.value.trim().length > 0) {
      this.scheduleSearch()
    }
  }

  queryChanged() {
    if (this.queryInputTarget.value !== this.selectedLabel) {
      this.hiddenInputTarget.value = ""
    }
    this.scheduleSearch()
  }

  scheduleSearch() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.#performSearch(), this.debounceValue)
  }

  keydown(event) {
    if (!this.#isOpen()) {
      if (event.key === "ArrowDown" && this.queryInputTarget.value.trim().length > 0) {
        this.#performSearch()
      }
      return
    }

    const items = this.#itemElements()
    if (event.key === "Escape") {
      event.preventDefault()
      this.#hideResults()
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (items.length === 0) return
      this.highlightIndex = Math.min(this.highlightIndex + 1, items.length - 1)
      this.#applyHighlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      if (items.length === 0) return
      this.highlightIndex = Math.max(this.highlightIndex - 1, 0)
      this.#applyHighlight(items)
    } else if (event.key === "Enter") {
      event.preventDefault()
      const pick = items[this.highlightIndex] || items[0]
      if (pick) this.#choose(parseInt(pick.dataset.id, 10), pick.dataset.label || pick.textContent)
    }
  }

  selectFromEvent(event) {
    event.preventDefault()
    const el = event.currentTarget
    this.#choose(parseInt(el.dataset.id, 10), el.dataset.label || el.textContent)
  }

  #closeIfOutside(event) {
    if (!this.element.contains(event.target)) this.#hideResults()
  }

  async #performSearch() {
    const q = this.queryInputTarget.value.trim()
    this.#abortFetch()
    if (q.length === 0) {
      this.#hideResults()
      return
    }

    this.abortController = new AbortController()
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", q)

    try {
      const res = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!res.ok) return
      const data = await res.json()
      this.#renderResults(data)
    } catch (e) {
      if (e.name !== "AbortError") throw e
    }
  }

  #renderResults(rows) {
    this.resultsTarget.innerHTML = ""
    this.highlightIndex = rows.length > 0 ? 0 : -1

    if (rows.length === 0) {
      const li = document.createElement("li")
      li.className = "px-3 py-2 text-sm text-base-content/60"
      li.textContent = "No matches"
      li.setAttribute("role", "presentation")
      this.resultsTarget.appendChild(li)
      this.resultsTarget.hidden = false
      return
    }

    rows.forEach((row, idx) => {
      const li = document.createElement("li")
      li.setAttribute("role", "option")
      li.dataset.id = row.id
      li.dataset.label = row.label
      li.dataset.action = "mousedown->customer-search#selectFromEvent"
      li.className =
        "cursor-pointer px-3 py-2 text-sm hover:bg-base-200 aria-selected:bg-base-200"
      li.textContent = row.label
      if (idx === 0) li.setAttribute("aria-selected", "true")
      this.resultsTarget.appendChild(li)
    })
    this.resultsTarget.hidden = false
  }

  #choose(id, label) {
    this.hiddenInputTarget.value = id
    this.queryInputTarget.value = label
    this.selectedLabel = label
    this.#hideResults()
  }

  #hideResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.hidden = true
    this.highlightIndex = -1
  }

  #isOpen() {
    return !this.resultsTarget.hidden
  }

  #itemElements() {
    return Array.from(this.resultsTarget.querySelectorAll("li[role='option'][data-id]"))
  }

  #applyHighlight(items) {
    items.forEach((el, i) => {
      if (i === this.highlightIndex) {
        el.setAttribute("aria-selected", "true")
        el.scrollIntoView({ block: "nearest" })
      } else {
        el.removeAttribute("aria-selected")
      }
    })
  }

  #abortFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }
}
